import { mainRouteMapping } from '../pages/Search/constants/mainRouteMapping';
import { COUNTRY_SLUGS, SITE_ORIGIN } from './constants';

const UNRESOLVED_NAMES = new Set(['', 'anywhere', 'unknown']);

export function slugToDisplayName(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function isUnresolvedEntityName(
  name: string | undefined | null,
): boolean {
  if (!name) {
    return true;
  }

  return UNRESOLVED_NAMES.has(name.trim().toLowerCase());
}

export function resolveCountryDisplayName(
  countrySlug: string | undefined,
): string {
  if (!countrySlug) {
    return '';
  }

  const normalized = countrySlug.toLowerCase();
  if (!COUNTRY_SLUGS.includes(normalized as (typeof COUNTRY_SLUGS)[number])) {
    return slugToDisplayName(countrySlug);
  }

  return slugToDisplayName(normalized);
}

export function resolveMainRouteDisplayName(
  mainRouteSlug: string | undefined,
): string {
  if (!mainRouteSlug) {
    return '';
  }

  return mainRouteMapping[mainRouteSlug]?.name ?? 'Unknown';
}

export function buildCountryPath(countrySlug: string): string {
  return `/destinations/${countrySlug.toLowerCase()}`;
}

export function buildMainRoutePath(mainRouteSlug: string): string {
  return `/routes/${mainRouteSlug.toLowerCase()}`;
}

export function buildCountryCanonical(countrySlug: string): string {
  return `${SITE_ORIGIN}${buildCountryPath(countrySlug)}`;
}

export function buildMainRouteCanonical(mainRouteSlug: string): string {
  return `${SITE_ORIGIN}${buildMainRoutePath(mainRouteSlug)}`;
}

export function buildBoatCanonical(boatSlug: string): string {
  return `${SITE_ORIGIN}/boat/${boatSlug.toLowerCase()}`;
}

export function buildCountryTitle(countryName: string): string {
  return `Liveaboard Diving in ${countryName}: Seasons, Routes, Prices & Boats | Santtai`;
}

export function buildMainRouteTitle(routeName: string): string {
  return `${routeName} Liveaboard Diving Trips: Best Season, Boats & Prices | Santtai`;
}

export function buildBoatTitle(boatName: string): string {
  return `${boatName} Liveaboard: Cabins, Itineraries, Inclusions & Prices | Santtai`;
}

export function buildCountryDescription(countryName: string): string {
  return `Compare liveaboard diving trips in ${countryName}. Explore routes, seasons, boats and prices. Book now, pay later with Santtai.`;
}

export function buildMainRouteDescription(routeName: string): string {
  return `Compare liveaboard diving trips to ${routeName}. See best season windows, boats, itineraries and prices. Book now, pay later with Santtai.`;
}

export function buildBoatDescription(boatName: string): string {
  return `Book a liveaboard diving trip aboard ${boatName}. View cabins, itineraries, inclusions and upcoming departures. Book now, pay later with Santtai.`;
}

export const SAFE_SEARCH_TITLE = 'Discover Liveaboard Diving Trips | Santtai';
export const SAFE_SEARCH_DESCRIPTION =
  'Find liveaboard diving trips worldwide. Compare boats, itineraries and prices. Book now, pay later with Santtai.';

export const THAILAND_GUIDE_TITLE =
  'Liveaboard Diving in Thailand — Complete Guide | Santtai';
export const THAILAND_GUIDE_DESCRIPTION =
  'Plan your Thailand liveaboard diving trip. Explore North & South Andaman and Koh Tao routes, compare boats, check dive sites and best seasons.';

export type SearchPageMetadata = {
  title: string;
  description: string;
  canonicalUrl: string | null;
  robots: 'index, follow' | 'noindex, follow';
  entityName: string;
};

export function getSearchPageMetadata(params: {
  countryName?: string;
  mainRoute?: string;
}): SearchPageMetadata {
  const { countryName, mainRoute } = params;

  if (mainRoute) {
    const entityName = resolveMainRouteDisplayName(mainRoute);
    const isPublishable = !isUnresolvedEntityName(entityName);

    return {
      entityName,
      title: isPublishable
        ? buildMainRouteTitle(entityName)
        : SAFE_SEARCH_TITLE,
      description: isPublishable
        ? buildMainRouteDescription(entityName)
        : SAFE_SEARCH_DESCRIPTION,
      canonicalUrl: isPublishable ? buildMainRouteCanonical(mainRoute) : null,
      robots: isPublishable ? 'index, follow' : 'noindex, follow',
    };
  }

  if (countryName) {
    const entityName = resolveCountryDisplayName(countryName);
    const isPublishable = !isUnresolvedEntityName(entityName);

    return {
      entityName,
      title: isPublishable ? buildCountryTitle(entityName) : SAFE_SEARCH_TITLE,
      description: isPublishable
        ? buildCountryDescription(entityName)
        : SAFE_SEARCH_DESCRIPTION,
      canonicalUrl: isPublishable ? buildCountryCanonical(countryName) : null,
      robots: isPublishable ? 'index, follow' : 'noindex, follow',
    };
  }

  return {
    entityName: '',
    title: SAFE_SEARCH_TITLE,
    description: SAFE_SEARCH_DESCRIPTION,
    canonicalUrl: null,
    robots: 'noindex, follow',
  };
}

export type BoatPageMetadata = {
  title: string;
  description: string;
  canonicalUrl: string | null;
  robots: 'index, follow' | 'noindex, follow';
};

export function getBoatPageMetadata(params: {
  boatSlug?: string;
  boatName?: string;
  hasToken: boolean;
  isLoading: boolean;
}): BoatPageMetadata {
  const { boatSlug, boatName, hasToken, isLoading } = params;

  if (hasToken || !boatSlug) {
    return {
      title: SAFE_SEARCH_TITLE,
      description: SAFE_SEARCH_DESCRIPTION,
      canonicalUrl: null,
      robots: 'noindex, follow',
    };
  }

  const canonicalUrl = buildBoatCanonical(boatSlug);

  if (isLoading || !boatName || isUnresolvedEntityName(boatName)) {
    return {
      title: buildBoatTitle(boatSlugToPlaceholderName(boatSlug)),
      description: buildBoatDescription(slugToDisplayName(boatSlug)),
      canonicalUrl,
      robots: 'index, follow',
    };
  }

  return {
    title: buildBoatTitle(boatName),
    description: buildBoatDescription(boatName),
    canonicalUrl,
    robots: 'index, follow',
  };
}

function boatSlugToPlaceholderName(boatSlug: string): string {
  return slugToDisplayName(boatSlug.replace(/^mv-|^msy-|^my-/i, ''));
}
